import * as Sentry from "@sentry/react-native";
import React, { useEffect, useRef } from "react";
import * as Updates from "expo-updates";
import { useRouter } from "expo-router";
import { AppState, type NativeEventSubscription } from "react-native";

import { isNewAppVersionAvailable } from "@/hooks/updates";
import { showAppUpdateAlert } from "@/utils/alerts/app_updates";

function UpdateProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  // Store subscription in useRef instead of a regular variable
  // This ensures the reference persists across re-renders
  const subscriptionRef = useRef<NativeEventSubscription | null>(null);
  const appState = useRef(AppState.currentState);
  const router = useRouter();
  const { isUpdateAvailable, availableUpdate } = Updates.useUpdates();

  // No op update

  const checkForUpdates = async (): Promise<void> => {
    try {
      if (__DEV__) {
        return; // Skip update checks in development mode
      }
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        router.replace("/app-update");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const checkForNewVersion = async (): Promise<void> => {
    const isAvailable = await isNewAppVersionAvailable();
    if (isAvailable) {
      showAppUpdateAlert(true);
    }
  };

  // Check for app updates whenever the app is in the foreground by using event listener.
  useEffect(() => {
    // Subscribe to AppState changes
    subscriptionRef.current = AppState.addEventListener(
      "change",
      (nextAppState) => {
        if (nextAppState === "active") {
          checkForUpdates();
          checkForNewVersion();
        }
        appState.current = nextAppState;
      },
    );

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.remove();
        subscriptionRef.current = null;
      }
    };
  }, []);

  // Check for app update while the user uses the app (via re-renders).
  useEffect(() => {
    if (isUpdateAvailable && availableUpdate) {
      checkForNewVersion();
      checkForUpdates();
    }
  }, [isUpdateAvailable]);

  return <>{children}</>;
}

export default UpdateProvider;
